import React, { useState, useEffect } from 'react';
import { getImageUrl } from '../utils/imageUtils';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { MessageCircle, Send, Trash2, Edit2, X, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';

export default function PhotoComments({ photoId, photoOwnerId }) {
  const { user, api } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');

  useEffect(() => {
    if (photoId) {
      fetchComments();
    }
  }, [photoId]);

  const fetchComments = async () => {
    try {
      const response = await api.get(`/photos/${photoId}/comments`);
      setComments(response.data);
    } catch (error) {
      // Non-subscribers might not have access
      if (error.response?.status !== 403) {
        console.error('Error fetching comments:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      const response = await api.post(`/photos/${photoId}/comments`, {
        content: newComment.trim()
      });
      setComments([response.data, ...comments]);
      setNewComment('');
      toast.success('Commentaire ajouté');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout du commentaire');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteComment = async (commentId) => {
    try {
      await api.delete(`/photos/${photoId}/comments/${commentId}`);
      setComments(comments.filter(c => c.id !== commentId));
      toast.success('Commentaire supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const startEdit = (comment) => {
    setEditingId(comment.id);
    setEditContent(comment.content);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditContent('');
  };

  const saveEdit = async (commentId) => {
    if (!editContent.trim()) return;

    try {
      const response = await api.put(`/photos/${photoId}/comments/${commentId}`, {
        content: editContent.trim()
      });
      setComments(comments.map(c => c.id === commentId ? response.data : c));
      setEditingId(null);
      setEditContent('');
      toast.success('Commentaire modifié');
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'À l\'instant';
    if (diff < 3600000) return `Il y a ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `Il y a ${Math.floor(diff / 3600000)}h`;
    if (diff < 604800000) return `Il y a ${Math.floor(diff / 86400000)}j`;
    
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <div className="w-5 h-5 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="border-t border-white/10 mt-4 pt-4" data-testid="photo-comments">
      {/* Comments header */}
      <div className="flex items-center gap-2 mb-4">
        <MessageCircle className="w-4 h-4 text-gold" />
        <span className="text-sm text-gray-400">
          {comments.length} commentaire{comments.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Add comment form */}
      {user?.is_subscribed && (
        <form onSubmit={submitComment} className="mb-4">
          <div className="flex gap-3">
            <Avatar className="w-8 h-8 flex-shrink-0">
              <AvatarImage src={user.profile_picture ? getImageUrl(user.profile_picture) : null} />
              <AvatarFallback className="bg-gold/20 text-gold text-xs">
                {user.pseudo?.[0]?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 flex gap-2">
              <Textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Ajouter un commentaire..."
                className="input-gold min-h-[40px] max-h-[100px] resize-none text-sm"
                rows={1}
                data-testid="comment-input"
              />
              <Button 
                type="submit" 
                disabled={!newComment.trim() || submitting}
                className="btn-gold px-3"
                data-testid="submit-comment-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </form>
      )}

      {!user?.is_subscribed && (
        <div className="text-center py-3 text-gray-500 text-sm bg-white/5 rounded-lg mb-4">
          Abonnez-vous pour commenter les photos
        </div>
      )}

      {/* Comments list */}
      <div className="space-y-4 max-h-[300px] overflow-y-auto scrollbar-visible">
        {comments.length === 0 ? (
          <p className="text-center text-gray-500 text-sm py-4">
            Aucun commentaire pour le moment
          </p>
        ) : (
          comments.map((comment) => (
            <div key={comment.id} className="flex gap-3 group" data-testid={`comment-${comment.id}`}>
              <Avatar className="w-8 h-8 flex-shrink-0">
                <AvatarImage src={comment.profile_picture ? getImageUrl(comment.profile_picture) : null} />
                <AvatarFallback className="bg-gold/20 text-gold text-xs">
                  {comment.pseudo?.[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-white">{comment.pseudo}</span>
                  <span className="text-xs text-gray-500">{formatDate(comment.created_at)}</span>
                  {comment.updated_at && (
                    <span className="text-xs text-gray-600">(modifié)</span>
                  )}
                </div>
                
                {editingId === comment.id ? (
                  <div className="flex gap-2">
                    <Textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      className="input-gold text-sm min-h-[40px] resize-none"
                      rows={2}
                    />
                    <div className="flex flex-col gap-1">
                      <Button 
                        size="sm" 
                        onClick={() => saveEdit(comment.id)}
                        className="btn-gold px-2 py-1"
                      >
                        <Check className="w-3 h-3" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={cancelEdit}
                        className="text-gray-400 hover:text-white px-2 py-1"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-300 break-words">{comment.content}</p>
                )}
              </div>
              
              {/* Actions */}
              {!editingId && (comment.user_id === user?.id || user?.is_admin) && (
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {comment.user_id === user?.id && (
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => startEdit(comment)}
                      className="text-gray-400 hover:text-gold p-1 h-auto"
                    >
                      <Edit2 className="w-3 h-3" />
                    </Button>
                  )}
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => deleteComment(comment.id)}
                    className="text-gray-400 hover:text-red-400 p-1 h-auto"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
